import { eachMinuteOfInterval, format } from 'date-fns';

/**
 * Validate the start and end hours.
 * @param startHour
 * @param endHour
 * @returns true if the start and end hours are valid, false otherwise
 */
const _isValidHourDuration = (startHour: number, endHour: number): boolean => {
  if (startHour < 0 || startHour > 24) {
    return false;
  }
  if (endHour < 0 || endHour > 24) {
    return false;
  }
  if (startHour >= endHour) {
    return false;
  }
  return true;
};

/**
 * Get time segments between the start and end hours.
 * @param startHour The start hour of the time segments (e.g. 5)
 * @param endHour The end hour of the time segments (e.g. 24)
 * @param segmentsPerHour The number of segments per hour (e.g. 2 for 30-minute segments)
 * @returns An array of time segments (e.g. ['04:00', '04:30', '05:00', '05:30', ...])
 */
export function getTimeSegments(startHour: number, endHour: number, segmentsPerHour: number, includeEnd = false): string[] {
  const segments: string[] = [];
  if (!_isValidHourDuration(startHour, endHour)) return segments;

  for (let hour = startHour; hour < endHour; hour++) {
    for (let hourSegment = 0; hourSegment < segmentsPerHour; hourSegment++) {
      segments.push(`${hour.toString()}:${(hourSegment * (60 / segmentsPerHour)).toString().padStart(2, '0')}`);
    }
  }
  if (includeEnd) {
    segments.push(`${endHour.toString()}:00`);
  }
  return segments;
}

/**
 * Get time intervals between the start and end hours v2.
 * This uses date-fns to get the time intervals.
 *
 *
 * @param config The configuration object
 * @param config.startHour The start hour of the time segments (e.g. 5)
 * @param config.startMinute The start minute of the time segments (e.g. 30)
 * @param config.endHour The end hour of the time segments (e.g. 24)
 * @param config.endMinute The end minute of the time segments (e.g. 0)
 * @param config.interval The interval (in minutes) between time segments (e.g. 30)
 *
 * @returns An array of time intervals (e.g. ['04:00', '04:30', '05:00', '05:30', ...])
 */
export function getTimeIntervals(config: ITimeSlotConfig): string[] {
  const { startHour, startMinute, endHour, endMinute, interval } = config;
  const segments: string[] = [];
  if (!_isValidHourDuration(startHour, endHour)) return segments;

  const startDate = new Date();
  startDate.setHours(startHour);
  startDate.setMinutes(startMinute);
  const endDate = new Date();
  endDate.setHours(endHour);
  endDate.setMinutes(endMinute);

  const intervals = eachMinuteOfInterval({ start: startDate, end: endDate }, { step: interval });
  intervals.forEach((interval) => {
    segments.push(format(interval, 'HH:mm'));
  });
  return segments;
}

/**
 * Remove time from date.
 *
 * @param date
 * @returns {Date}
 */
export const removeTime = (date: Date | string): Date => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Whether the date is between given dates.
 * @param date
 * @param startDate
 * @param endDate
 * @returns {boolean}
 */
export const isBetween = (date: Date, startDate: Date, endDate: Date) => date >= startDate && date <= endDate;

/**
 * Config model for the getTimeSlots function
 */
export interface ITimeSlotConfig {
  /**
   * The start hour of the time segments (e.g. 5)
   */
  startHour: number;
  /**
   * The start minute of the time segments (e.g. 30)
   */
  startMinute: number;
  /**
   * The end hour of the time segments (e.g. 24)
   */
  endHour: number;
  /**
   * The end minute of the time segments (e.g. 0)
   */
  endMinute: number;
  /**
   * The interval (in minutes) between time segments (e.g. 30)
   */
  interval: number;
}
